/**
 * Определение домена куки авторизации на основе списка известных базовых доменов.
 *
 * Используется хардкод регистрируемых доменов (eTLD+1), на которых работает ученическое веб-приложение.
 * Список может быть расширен через переменную окружения
 * REACT_APP_AUTH_COOKIE_DOMAINS (домены разделяются символом "|").
 */

/**
 * Базовые регистрируемые домены (eTLD+1), на которых работает приложение.
 * Список синхронизирован с CORS-доменами серверной части (skillzrun-api).
 */
const KNOWN_BASE_DOMAINS = [];

/**
 * Возвращает объединённый список базовых доменов: хардкод плюс значения из
 * переменной окружения REACT_APP_AUTH_COOKIE_DOMAINS (через "|").
 *
 * @returns {string[]} Массив базовых доменов в нижнем регистре.
 */
function getBaseDomains() {
  const envDomains = process.env.REACT_APP_AUTH_COOKIE_DOMAINS;
  const extra = envDomains
    ? envDomains
        .split("|")
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean)
    : [];
  return [...KNOWN_BASE_DOMAINS, ...extra];
}

/**
 * Возвращает имя куки авторизации с учётом окружения.
 *
 * Имя отличается от имени куки административной панели ("admin_auth"/"dev_admin_auth"),
 * чтобы токены сотрудника и ученика не перезаписывали друг друга на общем домене.
 * Дополнительно dev-серверы (dev.docker.web.skillzrun.com, dev2.skillzrun.com и т.п. —
 * первая метка хоста начинается с "dev") используют своё имя, чтобы dev- и
 * production-версии приложения не перезаписывали токены друг друга.
 *
 * @returns {string} Имя куки авторизации.
 */
function getAuthCookieName() {
  const firstHostLabel = window.location.hostname.toLowerCase().split(".")[0];
  return firstHostLabel.startsWith("dev") ? "dev_auth" : "auth";
}

/**
 * Возвращает домен куки авторизации (eTLD+1 с ведущей точкой) для текущего
 * хоста, либо null, если хост не соответствует ни одному известному домену
 * (локальная разработка, IP-адрес и т.п.).
 *
 * Логика:
 *   dev.docker.skillzrun.com  -> .skillzrun.com
 *   app.amazonpilots.kz       -> .amazonpilots.kz
 *   unique.net.ua             -> null  (хост совпадает с базовым доменом)
 *   localhost                 -> null
 *
 * @returns {?string} Домен куки в формате ".example.com" либо null.
 */
function getAuthCookieDomain() {
  const hostname = window.location.hostname.toLowerCase();
  const baseDomains = getBaseDomains();

  for (const base of baseDomains) {
    // Хост совпадает с базовым доменом — поддоменов нет, куку оставляем хостовой.
    if (hostname === base) {
      return null;
    }
    // Хост является поддоменом базового домена — возвращаем домен с ведущей точкой.
    if (hostname.endsWith(`.${base}`)) {
      return `.${base}`;
    }
  }

  return null;
}

export { getAuthCookieName };
export default getAuthCookieDomain;
